/**
 * Einleitung auf der Startseite, solange die Anwendung leer ist.
 *
 * Eine frisch eingerichtete Installation zeigt sonst nur leere Listen, und
 * wer sie zum ersten Mal öffnet, weiß nicht, wo er anfangen soll. Statt eines
 * Willkommenstextes steht hier eine Liste mit dem, was tatsächlich fehlt —
 * abgehakt wird, was schon da ist.
 *
 * Sie verschwindet von selbst, sobald eine Prüfung angelegt wurde. Kein
 * Wegklicken nötig: was erledigt ist, soll nicht weiter im Weg stehen, und
 * ein Hinweis, den man wegklicken muss, kommt bei der nächsten Installation
 * wieder.
 */

import { Link } from 'react-router-dom'
import { darfVerwalten, useKonto } from '../konto/KontoKontext'

type Schritt = {
  text: string
  erledigt: boolean
  pfad: string
  knopf: string
  erklaerung: string
  /** Nur für Verwaltungsrollen sichtbar. */
  nurVerwaltung?: boolean
}

export function ErsteSchritte({
  kunden,
  plaetze,
  pruefungen,
  unternehmenVollstaendig,
}: {
  kunden: number
  plaetze: number
  pruefungen: number
  /** Reichen die Angaben für eine Rechnung? Undefined, wenn nicht abfragbar. */
  unternehmenVollstaendig?: boolean
}) {
  const { konto } = useKonto()

  // Sobald eine Prüfung läuft, ist die Anwendung in Betrieb – dann hat die
  // Einleitung ihren Zweck erfüllt.
  if (pruefungen > 0) return null

  const schritte: Schritt[] = [
    {
      text: 'Angaben zum Prüfbüro vervollständigen',
      erledigt: unternehmenVollstaendig === true,
      pfad: '/verwaltung',
      knopf: 'Zur Verwaltung',
      erklaerung: 'Anschrift und Zertifikat stehen auf jedem Bericht.',
      nurVerwaltung: true,
    },
    {
      text: 'Ersten Kunden anlegen',
      erledigt: kunden > 0,
      pfad: '/kunden',
      knopf: 'Kunden',
      erklaerung: 'Gemeinde, Kita, Schule – wem der Spielplatz gehört.',
    },
    {
      text: 'Spielplatz mit Geräten anlegen',
      erledigt: plaetze > 0,
      pfad: '/kunden',
      knopf: 'Kunden',
      erklaerung:
        'Der Gerätebestand gehört zum Platz und wird von jeder Prüfung erneut beurteilt.',
    },
    {
      text: 'Erste Prüfung anlegen',
      erledigt: false,
      pfad: '/pruefungen',
      knopf: 'Prüfungen',
      erklaerung: 'Daraus entsteht der Prüfbogen, und am Ende der Bericht.',
    },
  ].filter((s) => !s.nurVerwaltung || darfVerwalten(konto))

  const offen = schritte.find((s) => !s.erledigt)

  return (
    <section className="karte-akzent">
      <h2 className="text-xl font-bold">Erste Schritte</h2>
      <p className="mt-1 text-slate-800">
        Die Anwendung ist eingerichtet, aber noch leer. Diese vier Schritte führen
        zur ersten Prüfung.
      </p>

      <ol className="mt-3 space-y-2">
        {schritte.map((s) => {
          const dran = s === offen
          return (
            <li
              key={s.text}
              className={`flex flex-wrap items-start gap-3 rounded-feld p-2 ${
                dran ? 'bg-white' : ''
              }`}
            >
              <span
                aria-hidden
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-base font-bold ${
                  s.erledigt
                    ? 'bg-stufe-ob text-white'
                    : dran
                      ? 'bg-akzent-500 text-marke-900'
                      : 'bg-slate-300 text-slate-700'
                }`}
              >
                {s.erledigt ? '✓' : schritte.indexOf(s) + 1}
              </span>

              <span className="min-w-0 flex-1">
                <span
                  className={`block font-semibold ${
                    s.erledigt ? 'text-slate-500 line-through' : ''
                  }`}
                >
                  {s.text}
                </span>
                {!s.erledigt && (
                  <span className="block text-base text-slate-700">{s.erklaerung}</span>
                )}
              </span>

              {dran && (
                <Link to={s.pfad} className="knopf-haupt px-3 py-1" style={{ minHeight: '40px' }}>
                  {s.knopf}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}
